import React, { useState } from "react";
import { CameraOff, RefreshCw, ArrowRight, Smartphone, QrCode } from "lucide-react";
import { useSecondaryCamera, checkSecondaryCameraLive } from "../../../../hooks/useSecondaryCamera";
import { ROUNDS } from "../../../../constants/secondaryCamera";

// Mid-interview disconnect gate. Not the one-shot acknowledge modal — Continue
// only proceeds once a server check confirms the camera is actually back.
const SecondaryCameraRecoveryGate = ({ interviewId, onRecovered, whitelabelEnabled }) => {
  const [checking, setChecking] = useState(false);
  const [notDetected, setNotDetected] = useState(false);

  const { qrCode, qrLoading, qrError, generateSecondaryQR } = useSecondaryCamera({
    interviewId,
    isSecondaryCameraEnabled: true,
    round: ROUNDS.INTERVIEW,
  });

  const handleContinue = async () => {
    setChecking(true);
    setNotDetected(false);
    const live = await checkSecondaryCameraLive(interviewId, ROUNDS.INTERVIEW);
    setChecking(false);
    if (live) {
      onRecovered();
    } else {
      setNotDetected(true);
    }
  };

  const headingText = whitelabelEnabled ? "text-gray-800" : "text-[#B3B3B3]";
  const mutedText = whitelabelEnabled ? "text-gray-600" : "text-[#A7A7A7]";
  const cardStyle = whitelabelEnabled ? "bg-gray-50 border-gray-200" : "bg-white/5 border-gray-700/50";

  return (
    <div className="flex flex-col items-center justify-center h-full py-8 px-4 text-center">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5 bg-red-500/15">
        <CameraOff className="w-8 h-8 text-red-400" />
      </div>

      <h2 className={`text-2xl font-semibold mb-2 ${headingText}`}>
        Secondary camera disconnected
      </h2>
      <p className={`text-sm max-w-xl mb-8 ${mutedText}`}>
        Pick whichever is easier. The interview continues as soon as either one connects.
        Your progress is saved.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start w-full max-w-2xl mb-8">
        <div className={`rounded-xl border p-5 text-left ${cardStyle}`}>
          <div className="flex items-center gap-2 mb-2">
            <Smartphone className="w-4 h-4 text-blue-400" />
            <h3 className={`text-sm font-semibold ${headingText}`}>Same phone</h3>
          </div>
          <p className={`text-sm leading-relaxed ${mutedText}`}>
            Reopen the camera page on your phone. You're set once it shows{" "}
            <span className="font-medium">Monitoring</span>.
          </p>
          <p className={`text-xs mt-2 ${mutedText}`}>Can't find that tab? Use the QR instead.</p>
        </div>

        <div className={`rounded-xl border p-5 text-left ${cardStyle}`}>
          <div className="flex items-center gap-2 mb-2">
            <QrCode className="w-4 h-4 text-pink-400" />
            <h3 className={`text-sm font-semibold ${headingText}`}>Any other phone</h3>
          </div>
          <p className={`text-sm leading-relaxed ${mutedText}`}>
            {qrCode?.qr_code
              ? "Scan this with any phone to reconnect."
              : "Get a fresh QR code and scan it with any phone."}
          </p>
          {qrCode?.qr_code ? (
            <img
              src={qrCode.qr_code}
              alt="New secondary camera QR code"
              className="w-36 h-36 bg-white p-2 rounded-lg mx-auto mt-3"
            />
          ) : (
            <>
              <button
                onClick={() => generateSecondaryQR(true)}
                disabled={qrLoading}
                className={`text-sm font-medium flex items-center gap-1.5 mt-3 ${
                  qrLoading ? "opacity-50 cursor-not-allowed" : "text-blue-500 hover:text-blue-400"
                }`}
              >
                <RefreshCw className={`w-3.5 h-3.5 ${qrLoading ? "animate-spin" : ""}`} />
                {qrLoading ? "Generating..." : "Show QR code"}
              </button>
              {qrError && <p className="text-sm text-red-400 mt-2">{qrError}</p>}
            </>
          )}
        </div>
      </div>

      {notDetected && (
        <p className="text-sm text-amber-500 mb-4">
          Camera not detected yet. Make sure your phone shows Monitoring, then try again.
        </p>
      )}

      <button
        onClick={handleContinue}
        disabled={checking}
        className={`px-8 py-3 rounded-full font-medium flex items-center gap-2 text-white ${
          checking ? "bg-gray-600/60 cursor-wait" : "bg-gradient-to-r from-[#2F64FF] to-[#FB54AE]"
        }`}
      >
        {checking ? "Checking..." : "Continue interview"}
        {!checking && <ArrowRight className="w-4 h-4" />}
      </button>
    </div>
  );
};

export default SecondaryCameraRecoveryGate;
